import { SuggestionData } from 'ckeditor5-premium-features'
import { useSuggestionsStore } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'
import React, { memo, useMemo, useRef, useState } from 'react'
import { CustomSuggestionCardWrapper } from './CustomSuggestionCardWrapper'
import { useContainerHeight } from './hooks/useConatinerHeight'
import { useHighlightVirtualizedCard } from './hooks/useHighlightVirtualizedCard'
import { useResetScrollOnRequest } from './hooks/useResetScrollOnRequest'
import { useVirtualScroll } from './hooks/useVirtualScroll'

interface VirtualizedSuggestionListProps {
    suggestions: SuggestionData[]
    trackChangesData: Record<string, TrackedChange>
    containerHeight?: number
    isCustomFilterActive?: boolean
}
const defaultContainerHeight = 1000
const heightBuffer = 90
/**
 * VirtualizedSuggestionList component renders a list of suggestions
 * using virtualization techniques to improve performance.
 * It calculates visible suggestions based on scroll position
 * and efficiently updates the UI.
 * @param {VirtualizedSuggestionListProps} props - The properties for the component.
 * @return {JSX.Element} The rendered virtualized suggestion list.
 */
export const VirtualizedSuggestionList = memo(
    ({
        suggestions,
        trackChangesData,
        isCustomFilterActive = false,
    }: VirtualizedSuggestionListProps) => {
        const listRef = useRef<HTMLDivElement>(null)
        const itemHeightsRef = useRef<number[]>([])
        const [itemOffsets, setItemOffsets] = useState<number[]>([])
        const activeCardFilter = useSuggestionsStore(
            (s) => s.suggestionCardFilter
        )

        const containerHeight = useContainerHeight(
            '[data-testid="tree-layout"]',
            defaultContainerHeight
        )
        const visibleIndexes = useVirtualScroll({
            containerRef: listRef,
            positions: itemOffsets,
            suggestionsLength: suggestions.length,
            containerHeight,
        })

        // Calculate total height for absolute positioning
        const scrollHeight = useMemo(() => {
            return itemOffsets.length > 0
                ? itemOffsets[itemOffsets.length - 1] +
                      (itemHeightsRef.current[
                          itemHeightsRef.current.length - 1
                      ] || 0)
                : 0
        }, [
            itemOffsets.length,
            itemHeightsRef.current.length,
            itemHeightsRef.current[itemHeightsRef.current.length - 1],
            itemOffsets[itemOffsets.length - 1],
        ])

        // Memoize visibleSuggestions
        const visibleSuggestions = useMemo(
            () =>
                suggestions.slice(
                    Math.max(0, visibleIndexes.start),
                    Math.min(visibleIndexes.end, suggestions.length)
                ),
            [suggestions, visibleIndexes]
        )
        const viewportHeight = containerHeight + heightBuffer

        useHighlightVirtualizedCard(suggestions, itemOffsets, listRef)
        useResetScrollOnRequest(listRef, isCustomFilterActive)

        return (
            <CustomSuggestionCardWrapper
                visibleSuggestions={visibleSuggestions}
                containerRef={listRef}
                containerHeight={viewportHeight}
                totalHeight={scrollHeight}
                heightsRef={itemHeightsRef}
                suggestions={suggestions}
                visibleRange={visibleIndexes}
                setPositions={setItemOffsets}
                isNotDefaultSuggestionCardFilter={isCustomFilterActive}
                positions={itemOffsets}
                trackChangesData={trackChangesData}
                suggestionCardFilter={activeCardFilter}
            />
        )
    }
)
